import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Database, ChevronLeft, ChevronRight } from 'lucide-react';
import { workerQueryAPI, radiusServersAPI } from '../api';
import { Card, PageHeader, Alert, Spinner, Button, useSort } from '../components/UI';

const TABLE_COLORS = {
  radcheck:      'var(--accent-blue)',
  radreply:      'var(--accent-cyan)',
  radusergroup:  'var(--accent-purple)',
  radgroupcheck: 'var(--accent-orange)',
  radgroupreply: 'var(--accent-green)',
  nas:           'var(--accent-red)',
  radacct:       '#f472b6',
  radpostauth:   '#a78bfa',
  realm:         'var(--text-secondary)',
};

const TABLE_DESC = {
  radcheck:      'User check attributes',
  radreply:      'User reply attributes',
  radusergroup:  'User ↔ group mapping',
  radgroupcheck: 'Group check attributes',
  radgroupreply: 'Group reply attributes',
  nas:           'NAS devices',
  radacct:       'Accounting sessions',
  radpostauth:   'Post-auth log',
  realm:         'Realm / proxy config',
};

const FILTER_COLS = {
  radcheck:      ['username'],
  radreply:      ['username'],
  radusergroup:  ['username', 'groupname'],
  radgroupcheck: ['groupname'],
  radgroupreply: ['groupname'],
  nas:           ['nasname'],
  radacct:       ['username', 'nasname'],
  radpostauth:   ['username'],
  realm:         [],
};

export default function WorkerQuery() {
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState('');
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');

  const [filters, setFilters] = useState({ username: '', groupname: '', nasname: '' });
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tablesLoading, setTablesLoading] = useState(false);

  // load servers
  useEffect(() => {
    radiusServersAPI.list({ per_page: 100, status: 'active' })
      .then(r => setServers(r.radius_servers || [])).catch(() => {});
  }, []);

  // load tables when server selected
  useEffect(() => {
    if (!selectedServer) { setTables([]); setSelectedTable(''); return; }
    setTablesLoading(true);
    workerQueryAPI.tables(selectedServer)
      .then(r => { setTables(r.tables || []); })
      .catch(e => setError(e.message))
      .finally(() => setTablesLoading(false));
  }, [selectedServer]);

  const { sort, onSortChange, resetSort, sortParam } = useSort();

  const runQuery = useCallback(async (off = offset) => {
    if (!selectedServer || !selectedTable) return;
    setLoading(true); setError(null);
    try {
      const params = { limit, offset: off };
      if (filters.username)  params.username  = filters.username;
      if (filters.groupname) params.groupname = filters.groupname;
      if (filters.nasname)   params.nasname   = filters.nasname;
      if (sortParam) params.sort = sortParam;
      const res = await workerQueryAPI.query(selectedServer, selectedTable, params);
      setResult(res);
      setOffset(off);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [selectedServer, selectedTable, filters, limit, offset, sortParam]);

  const handleTableClick = (t) => {
    setSelectedTable(t);
    setResult(null);
    setOffset(0);
    setFilters({ username: '', groupname: '', nasname: '' });
    if (sort) resetSort(); // reset ke unsorted (kolom tabel baru beda struktur)
  };

  const handleSearch = () => { setOffset(0); runQuery(0); };
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  // auto-query when table selected atau sort berubah
  useEffect(() => {
    if (selectedTable) runQuery(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable, sortParam]);

  const availableFilters = FILTER_COLS[selectedTable] || [];
  const columns = result?.data?.length > 0 ? Object.keys(result.data[0]) : [];
  const totalPages = result ? Math.ceil(result.total / limit) : 0;
  const currentPage = result ? Math.floor(offset / limit) + 1 : 1;

  const inputStyle = {
    background: 'var(--bg-input)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: '7px 10px',
    color: 'var(--text-primary)', fontSize: 12, outline: 'none',
    fontFamily: 'var(--font-sans)', width: '100%',
  };

  return (
    <div className="animate-in">
      <PageHeader
        title="Server Query"
        subtitle="Query langsung ke database MySQL radius server"
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}

      {/* Server selector */}
      <div style={{ marginBottom: 16 }}>
        <select value={selectedServer} onChange={e => { setSelectedServer(+e.target.value || ''); setSelectedTable(''); setResult(null); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-primary)', fontSize: 13, minWidth: 240, cursor: 'pointer' }}>
          <option value="">— Pilih Radius Server —</option>
          {servers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.host})</option>)}
        </select>
      </div>

      {!selectedServer ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
            <Database size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
            <div style={{ fontSize: 13 }}>Pilih radius server untuk mulai query</div>
          </div>
        </Card>
      ) : (
        <div className="worker-query-layout" style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16, alignItems: 'start' }}>

          {/* Table list */}
          <Card style={{ padding: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 8px 8px' }}>
              Tables
            </div>
            {tablesLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><Spinner size={16} /></div>
            ) : (
              tables.map(t => (
                <button key={t} onClick={() => handleTableClick(t)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 10px', borderRadius: 'var(--radius)',
                    background: selectedTable === t ? `${TABLE_COLORS[t]}18` : 'none',
                    border: `1px solid ${selectedTable === t ? TABLE_COLORS[t] + '44' : 'transparent'}`,
                    color: selectedTable === t ? TABLE_COLORS[t] : 'var(--text-secondary)',
                    fontSize: 12, cursor: 'pointer', marginBottom: 2,
                    fontFamily: 'var(--font-mono)',
                    transition: 'all 0.1s',
                  }}
                  onMouseEnter={e => { if (selectedTable !== t) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                  onMouseLeave={e => { if (selectedTable !== t) e.currentTarget.style.background = 'none'; }}
                >
                  <div>{t}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', marginTop: 1 }}>{TABLE_DESC[t]}</div>
                </button>
              ))
            )}
          </Card>

          {/* Query area */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Filters */}
            {selectedTable && (
              <Card style={{ padding: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  {availableFilters.includes('username') && (
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Username</div>
                      <input style={inputStyle} placeholder="cari username…" value={filters.username}
                        onChange={e => setFilters(f => ({ ...f, username: e.target.value }))}
                        onKeyDown={handleKeyDown} />
                    </div>
                  )}
                  {availableFilters.includes('groupname') && (
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Groupname</div>
                      <input style={inputStyle} placeholder="cari groupname…" value={filters.groupname}
                        onChange={e => setFilters(f => ({ ...f, groupname: e.target.value }))}
                        onKeyDown={handleKeyDown} />
                    </div>
                  )}
                  {availableFilters.includes('nasname') && (
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>NAS Name / IP</div>
                      <input style={inputStyle} placeholder="cari nasname…" value={filters.nasname}
                        onChange={e => setFilters(f => ({ ...f, nasname: e.target.value }))}
                        onKeyDown={handleKeyDown} />
                    </div>
                  )}
                  <div style={{ minWidth: 80 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Limit</div>
                    <select value={limit} onChange={e => setLimit(+e.target.value)}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      {[50, 100, 250, 500, 1000].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <Button onClick={handleSearch} loading={loading} size="sm">
                    <Search size={13} /> Query
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setFilters({ username: '', groupname: '', nasname: '' }); setOffset(0); runQuery(0); }}>
                    Reset
                  </Button>
                </div>
              </Card>
            )}

            {/* Results */}
            <Card style={{ padding: 0 }}>
              {/* result header */}
              {result && (
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: TABLE_COLORS[selectedTable] || 'var(--accent-blue)' }}>
                      {selectedTable}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {result.message
                        ? result.message
                        : `${result.total.toLocaleString()} total rows — menampilkan ${offset + 1}–${Math.min(offset + limit, result.total)}`
                      }
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => runQuery(offset)} loading={loading}>
                    <RefreshCw size={12} />
                  </Button>
                </div>
              )}

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={24} /></div>
              ) : !result ? (
                <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
                  {selectedTable ? 'Klik Query untuk menjalankan' : 'Pilih tabel di kiri'}
                </div>
              ) : result.data?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
                  {result.message || 'Tidak ada data'}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {columns.map(col => {
                          const active = sort?.key === col;
                          return (
                            <th key={col} style={{ padding: '8px 12px', textAlign: 'left', color: active ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                              <button onClick={() => onSortChange(col)}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}>
                                {col}
                                <span style={{ fontSize: 9, opacity: active ? 1 : 0.35 }}>{active ? (sort.dir === 'desc' ? '▼' : '▲') : '⇅'}</span>
                              </button>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {result.data.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          {columns.map(col => (
                            <td key={col} style={{ padding: '7px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row[col] === null || row[col] === undefined
                                ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>null</span>
                                : typeof row[col] === 'object'
                                  ? <span className="mono" style={{ fontSize: 11 }}>{JSON.stringify(row[col])}</span>
                                  : <span className={['id','port','radacctid','acct_session_time','framedipaddress'].some(k => col.toLowerCase().includes(k)) ? 'mono' : ''}>
                                      {String(row[col])}
                                    </span>
                              }
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {result && result.total > limit && (
                <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                  <Button variant="ghost" size="sm" disabled={offset === 0}
                    onClick={() => runQuery(Math.max(0, offset - limit))}>
                    <ChevronLeft size={13} /> Prev
                  </Button>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {currentPage} / {totalPages}
                  </span>
                  <Button variant="ghost" size="sm" disabled={offset + limit >= result.total}
                    onClick={() => runQuery(offset + limit)}>
                    Next <ChevronRight size={13} />
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}